import { Log } from '@apparel/core/log';
import { useMounted } from '@hooks/useMounted';
import { app } from '@packages/app';
import { ContextOptions, ContextServices, createContextOptions } from '@packages/context/lib/contextOptions';
import { call } from '@packages/functions';
import { createMemo } from '@packages/react';
import { Record as ImRecord } from 'immutable';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import * as React from 'react';
import { NotificationServiceProvider } from '../../notifications';
import { RouterServiceProvider } from '../../router/lib/RouterServiceProvider';
import { Action, ActionsMap, mapActions } from './action';

export type ContextType<S, A> = [ImRecord<S>, ActionsMap<keyof A>];

export interface ProviderProps<S, A = Record<string, any>> {
    [key: string]: any;

    apiRef?: (ctx: ContextType<S, A>) => void | React.MutableRefObject<ContextType<S, A>>;
    value?: Partial<S>;
    children?: React.ReactNode;
    onChange?: (state: ImRecord<S>) => void;
}

interface CreateContextAPI<S, A> {
    Context: React.Context<ContextType<S, A>>;
    Provider: React.MemoExoticComponent<React.ComponentType<ProviderProps<S, A>>>;
}

const _onDefault = (s: any): any => s;

/**
 * TODO: Fix the value prop change (Alinus)
 * @param options
 */
export function createContext<S, A>(options: Partial<ContextOptions<S, A>> = {}): CreateContextAPI<S, A> {
    const options$ = createContextOptions(options as any);

    const logActions = Log.channel('Contexts/' + options$.contextName + '/actions').setActive(!!options$.debug.actions);
    const logEvents = Log.channel('Contexts/' + options$.contextName + '/events').setActive(!!options$.debug.events);
    const log = Log.channel('Contexts/' + options$.contextName + '/misc').setActive(!!options$.debug.misc);

    const Context = React.createContext<ContextType<S, A>>([options$.initialState() as any, {} as any]);

    function ProviderComponent(props: ProviderProps<S, A>): React.ReactElement {
        const isMounted = useMounted();
        const changed = React.useRef<boolean>(false);

        const [reducerState] = React.useState<ImRecord<S>>((): ImRecord<S> => {
            let state$ = props.value ? options$.initialState(props.value) : options$.initialState();

            if (options$.stateExtend) {
                log.debug('Extend state');
                state$ = options$.stateExtend(state$ as any, props);
            }

            log.debug('Create state');
            if (log.isActive()) {
                console.dir({ state: state$.toJS(), value: props.value });
            }

            return state$ as any;
        });

        const [services] = React.useState<ContextServices<keyof A>>(() => ({
            router: app(RouterServiceProvider),
            notify: app(NotificationServiceProvider),
            actions: {} as any,
        }));

        const reducer = React.useCallback<(prevState: ImRecord<S>, action: Action<any, keyof A>) => ImRecord<S>>(
            (prevState: ImRecord<S>, action: Action<any, keyof A>): ImRecord<S> => {
                if (!isMounted()) {
                    return prevState;
                }

                const eventName = `on${upperFirst(camelCase(action.type as string))}`;

                const eventFunc = options$.events.hasOwnProperty(eventName)
                    ? options$.events[eventName].bind(options$.events)
                    : options$.events.onDefault || _onDefault;

                const newState = eventFunc(prevState as any, action as any, {
                    ...services,
                    actions: actions as any,
                }) as any;

                logEvents.debug(`Call event ${eventName}`);

                if (logEvents.isActive()) {
                    console.dir({
                        action,
                        same: newState === prevState,
                        newState: newState.toJS(),
                        prevState: prevState.toJS(),
                    });
                }

                return (newState ? newState : prevState) as any;
            },
            [''],
        );

        const [state, reducerDispatch] = React.useReducer<
            (prevState: ImRecord<S>, action: Action<any, keyof A>) => ImRecord<S>
        >(reducer, reducerState);

        const dispatch = (type: keyof A, payload?: any, ...rest: any[]): void => {
            setTimeout(() => {
                if (isMounted()) {
                    logActions.debug('Call action [%s]', type);

                    if (logActions.isActive()) {
                        console.dir({ payload, rest });
                    }

                    // Dispatch action on the next tick if the Provider it's mounted
                    reducerDispatch({ type, payload, rest });
                }
            });
        };

        const [actions] = React.useState<ActionsMap<keyof A>>(() => {
            const actionsMap = mapActions<keyof A>(options$.actions as any, dispatch);
            logActions.debug('Create actions', actionsMap);
            return actionsMap as any;
        });

        actions.dispatch = dispatch;

        const contextState: ContextType<S, A> = [state, actions];

        React.useEffect(() => {
            if (props.apiRef) {
                if (typeof props.apiRef === 'function') {
                    props.apiRef(contextState);
                } else {
                    (props.apiRef as any).current = contextState;
                }
            }
        }, [state, dispatch, actions]);

        React.useEffect(() => {
            if (options$.events.onMount) {
                // @ts-ignore
                dispatch('mount');
            }

            return (): void => {
                if (options$.events.onUnMount) {
                    // @ts-ignore
                    dispatch('unMount');
                }
            };
        }, ['']);

        React.useEffect(() => {
            if (changed.current) {
                if (options$.onChange) {
                    log.debug('Call onChange');
                    if (log.isActive()) {
                        console.dir(state.toJS());
                    }

                    options$.onChange(state as any);
                }

                call(props.onChange, state);
            }

            // Avoid first call (mount call)
            changed.current = true;
        }, [state.hashCode()]);

        return <Context.Provider value={contextState}>{props.children}</Context.Provider>;
    }

    return {
        Context,
        Provider: createMemo(ProviderComponent, { displayName: `core.context(${options$.contextName})` }),
    };
}
